/**
 * Submission View Modal
 * Displays assignment or quiz submission in a modal overlay
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiX, FiFileText, FiUser, FiCalendar, FiAward, FiClock, FiCheckCircle, FiXCircle, FiDownload, FiEdit3, FiSave, FiCheck } from 'react-icons/fi';
import api from '../../services/api';
import quizApiService from '../../services/quizApi';
import { sanitizeHtmlForViewer } from '../../lib/richtext/htmlSanitizer';
import PdfViewer from '../common/PdfViewer';

// Local date formatting functions (matching Gradebook component)
const formatTimestamp = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const ModalContent = styled.div`
  background: var(--background-primary);
  border-radius: 0;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  
  @media (max-width: 768px) {
    max-height: 95vh;
    border-radius: 0;
  }
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--background-tertiary);
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: var(--text-muted);
  gap: 1rem;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: var(--error-color);
  gap: 1rem;
`;

const InfoSection = styled.div`
  background: var(--background-secondary);
  border-radius: 0;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  border: 1px solid var(--border-color);
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.9375rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: var(--text-muted);
  min-width: 100px;
  text-transform: uppercase;
  font-size: 0.8125rem;
`;

const InfoValue = styled.span`
  color: var(--text-primary);
  flex: 1;
`;

const SubmissionContent = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  margin-top: 1.5rem;
  
  & * {
    max-width: 100%;
  }
  
  img {
    max-width: 100%;
    height: auto;
  }
`;

const PdfButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 38px;
  
  &:hover {
    background: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(216, 184, 137, 0.3);
  }
`;

const PdfButtonContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  flex-wrap: wrap;
  align-items: center;
`;

const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 38px;
  
  &:hover {
    background: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(216, 184, 137, 0.3);
  }
`;

// Quiz-specific styles
const QuestionContainer = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid var(--border-color);
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const QuestionNumber = styled.div`
  background: var(--primary-color);
  color: var(--text-inverse);
  width: 32px;
  height: 32px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
`;

const QuestionText = styled.div`
  flex: 1;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
  line-height: 1.5;
`;

const AnswerSection = styled.div`
  margin-left: 3rem;
  margin-top: 1rem;
`;

const AnswerLabel = styled.div`
  font-weight: 600;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`;

const AnswerContent = styled.div`
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1rem;
  color: var(--text-primary);
`;

const CorrectBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.5rem;
  
  &.correct {
    background: rgba(104, 211, 145, 0.1);
    color: var(--success-color);
  }
  
  &.incorrect {
    background: rgba(252, 129, 129, 0.1);
    color: var(--error-color);
  }
`;

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--primary-color);
  background: var(--primary-color);
  color: var(--text-inverse);
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;

  &:hover {
    background: var(--primary-dark);
    border-color: var(--primary-dark);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  background: var(--background-primary);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }

  &.error {
    border-color: var(--error-color);
  }
`;

const EditTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  background: var(--background-primary);
  color: var(--text-primary);
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }

  &.error {
    border-color: var(--error-color);
  }
`;

const EditSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  background: var(--background-primary);
  color: var(--text-primary);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }

  &.error {
    border-color: var(--error-color);
  }

  option {
    padding: 0.5rem;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--background-primary);

  &:hover {
    border-color: var(--primary-color);
    background: rgba(216, 184, 137, 0.05);
  }

  &.selected {
    border-color: var(--primary-color);
    background: rgba(216, 184, 137, 0.1);
  }
`;

const RadioInput = styled.input`
  cursor: pointer;
`;

const RadioText = styled.span`
  flex: 1;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const FillBlankEditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FillBlankEditField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FillBlankLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
`;

const EditActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const RequiredField = styled.span`
  color: var(--error-color);
  margin-left: 0.25rem;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1.25rem;
  background: var(--background-secondary);
  border-radius: 0;
  border: 1px solid var(--border-color);
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FilterLabel = styled.label`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
  min-width: 120px;
`;

const FilterButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: ${props => props.active ? 'var(--primary-color)' : 'white'};
  color: ${props => props.active ? 'white' : 'var(--text-primary)'};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;

  &:hover {
    border-color: ${props => props.active ? 'var(--primary-color)' : 'var(--border-dark)'};
    background: ${props => props.active ? 'var(--primary-color)' : 'var(--background-secondary)'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.1);
  }
`;

const MatchingEditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const MatchingEditRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-items: center;
  padding: 0.75rem;
  background: var(--background-secondary);
  border-radius: 0;
  border: 1px solid var(--border-color);
`;

const MatchingTerm = styled.div`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const MatchingSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  background: var(--background-primary);
  color: var(--text-primary);
  cursor: pointer;
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.1);
  }

  &.error {
    border-color: var(--error-color);
  }
`;

function SubmissionViewModal({ isOpen, onClose, type, item, student }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submission, setSubmission] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideReasonExplanation, setOverrideReasonExplanation] = useState('');
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [answerFilter, setAnswerFilter] = useState('all'); // 'all', 'correct', 'incorrect'
  const [questionTypeFilter, setQuestionTypeFilter] = useState('all'); // 'all', 'matching', 'fill_blank', 'true_false', 'multiple_choice'
  const [extraCreditFilter, setExtraCreditFilter] = useState('all'); // 'all', 'extra_credit', 'regular'

  const fetchSubmission = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      if (type === 'assignment') {
        // For assignments, we need to get the submission from the submissions list
        const response = await api.getAssignmentSubmissions(item.assignment_id);
        if (response.success && response.submissions) {
          const studentSubmission = response.submissions.find(
            sub => sub.user_id === student.id
          );
          if (studentSubmission) {
            setSubmission({
              type: 'assignment',
              data: studentSubmission,
              assignment: response.assignment
            });
          } else {
            setError('Submission not found for this student');
          }
        } else {
          setError(response.error || 'Failed to load submission');
        }
      } else if (type === 'quiz') {
        // For quizzes, use attempt_id to get attempt details
        if (item.id) {
          const response = await quizApiService.getAttemptDetails(item.id);
          if (response.success && response.data) {
            setSubmission({
              type: 'quiz',
              data: response.data
            });
          } else {
            setError(response.message || 'Failed to load quiz submission');
          }
        } else {
          setError('Quiz attempt ID not found');
        }
      }
    } catch (err) {
      console.error('Error fetching submission:', err);
      setError('Failed to load submission. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [type, item, student]);

  useEffect(() => {
    if (isOpen && item && student) {
      fetchSubmission();
      // Reset filters when opening modal
      setAnswerFilter('all');
      setQuestionTypeFilter('all');
      setExtraCreditFilter('all');
    } else {
      setSubmission(null);
      setError('');
      // Reset filters when closing modal
      setAnswerFilter('all');
      setQuestionTypeFilter('all');
      setExtraCreditFilter('all');
    }
  }, [isOpen, item, student, fetchSubmission]);

  const processPdfData = (pdfData) => {
    let cleanBase64 = pdfData;
    
    // Remove data URI prefix if present
    if (cleanBase64.includes(',')) {
      cleanBase64 = cleanBase64.split(',')[1];
    }
    
    // Remove whitespace
    cleanBase64 = cleanBase64.replace(/\s/g, '');
    
    return cleanBase64;
  };

  const handleViewPdf = (pdfData) => {
    try {
      const cleanBase64 = processPdfData(pdfData);
      
      // Validate base64
      try {
        atob(cleanBase64);
        setSelectedPdf({
          pdfBase64: cleanBase64,
          fileName: `${submission?.assignment?.title || item?.title || 'submission'}_attachment.pdf`.replace(/[^a-z0-9]/gi, '_').toLowerCase()
        });
        setShowPdfViewer(true);
      } catch (decodeError) {
        console.error('Base64 decode error:', decodeError);
        setError('The PDF attachment data is not in a valid format.');
      }
    } catch (error) {
      console.error('Error processing PDF attachment:', error);
      setError('Failed to load PDF attachment. Please try again.');
    }
  };

  const handleDownloadPdf = (pdfData) => {
    try {
      const cleanBase64 = processPdfData(pdfData);
      
      // Validate base64
      try {
        atob(cleanBase64);
        const fileName = `${submission?.assignment?.title || item?.title || 'submission'}_attachment.pdf`.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        
        // Create download link
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${cleanBase64}`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (decodeError) {
        console.error('Base64 decode error:', decodeError);
        setError('The PDF attachment data is not in a valid format.');
      }
    } catch (error) {
      console.error('Error downloading PDF attachment:', error);
      setError('Failed to download PDF attachment. Please try again.');
    }
  };

  // Helper functions for fill-in-the-blank questions
  const getBlankCount = (prompt) => {
    if (!prompt) return 0;
    return (prompt.match(/_/g) || []).length;
  };

  const parseFillBlankAnswer = (answerText) => {
    if (!answerText) return [];
    if (typeof answerText === 'string' && answerText.includes('|')) {
      return answerText.split('|');
    }
    if (Array.isArray(answerText)) {
      return answerText;
    }
    return [answerText];
  };

  const formatFillBlankAnswer = (answerTexts) => {
    if (Array.isArray(answerTexts)) {
      return answerTexts.join('|');
    }
    return answerTexts || '';
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question.question_id);
    
    let editValues = {
      answer_text: '',
      selected_option_id: question.response_data?.selected_option_id || null
    };
    
    // For fill-in-the-blank questions, parse the answer into separate blanks
    if (question.question_type === 'fill_blank' || question.question_type === 'fill_in_the_blank') {
      const blankCount = getBlankCount(question.question_prompt);
      const parsedAnswers = parseFillBlankAnswer(question.your_answer || '');
      
      // Initialize answer_texts array with the correct number of blanks
      editValues.answer_texts = Array(blankCount).fill('').map((_, index) => 
        parsedAnswers[index] || ''
      );
    } else if (question.question_type === 'matching') {
      // For matching questions, initialize matching pairs
      try {
        let pairsToEdit = [];
        let foundAnswer = false;
        
        // First, try to get the original answer from response_data (this is the raw format)
        if (question.response_data?.answer_text) {
          try {
            const answerText = question.response_data.answer_text.trim();
            if (answerText) {
              const parsed = JSON.parse(answerText);
              if (Array.isArray(parsed) && parsed.length > 0) {
                // Validate that it has the correct structure
                if (parsed[0].leftIndex !== undefined && parsed[0].rightIndex !== undefined) {
                  pairsToEdit = parsed;
                  foundAnswer = true;
                }
              }
            }
          } catch (e) {
            console.warn('Failed to parse response_data.answer_text for matching question:', e);
          }
        }
        
        // If we don't have response_data, try your_answer
        if (!foundAnswer) {
          if (Array.isArray(question.your_answer) && question.your_answer.length > 0) {
            // Check if it's formatted with left/right or raw with leftIndex/rightIndex
            if (question.your_answer[0].left && question.your_answer[0].right) {
              // It's formatted - we can't convert back without settings, so use empty array
              // The user will need to re-enter the matches
              console.warn('Matching answer is in formatted format, cannot convert back to indices without question settings');
              pairsToEdit = [];
            } else if (question.your_answer[0].leftIndex !== undefined || question.your_answer[0].rightIndex !== undefined) {
              // It's already in index format
              pairsToEdit = question.your_answer;
              foundAnswer = true;
            }
          } else if (typeof question.your_answer === 'string' && question.your_answer.trim()) {
            // If it's a string, try to parse it
            try {
              const parsed = JSON.parse(question.your_answer);
              if (Array.isArray(parsed) && parsed.length > 0) {
                if (parsed[0].leftIndex !== undefined && parsed[0].rightIndex !== undefined) {
                  pairsToEdit = parsed;
                  foundAnswer = true;
                }
              }
            } catch (e) {
              console.warn('Failed to parse your_answer string for matching question:', e);
            }
          }
        }
        
        // Store pairs as an object map for easier editing: { leftIndex: rightIndex }
        const matchingPairs = {};
        pairsToEdit.forEach(pair => {
          if (pair.leftIndex !== undefined && pair.rightIndex !== undefined) {
            matchingPairs[pair.leftIndex] = pair.rightIndex;
          }
        });
        
        editValues.matchingPairs = matchingPairs;
      } catch (e) {
        console.error('Error processing matching question answer:', e);
        editValues.matchingPairs = {};
      }
    } else {
      // For other question types (short answer, essay), use the answer directly
      editValues.answer_text = question.your_answer || '';
    }
    
    setEditValues(editValues);
    
    // Parse existing override reason - check if it's one of the predefined options
    const existingReason = question.response_data?.instructor_override_reason || '';
    const predefinedReasons = [
      'Question Error',
      'Ambiguous Question',
      'Alternative Correct Answer',
      'Partial Credit Awarded',
      'Technical Issue',
      'Manual Review Required',
      'Student Accommodation',
      'Exceptional Circumstance',
      'Removed or Invalidated Question',
      'Other'
    ];
    
    // Check if existing reason starts with one of the predefined options
    let reason = '';
    let explanation = '';
    
    if (existingReason) {
      const matchedReason = predefinedReasons.find(r => existingReason.startsWith(r));
      if (matchedReason) {
        reason = matchedReason;
        if (matchedReason === 'Other' && existingReason.length > matchedReason.length) {
          // Extract explanation after "Other - " or "Other: " or just "Other"
          explanation = existingReason.replace(/^Other\s*[-:]\s*/, '').trim();
        }
      } else {
        // If it doesn't match any predefined, treat as "Other" with explanation
        reason = 'Other';
        explanation = existingReason;
      }
    }
    
    setOverrideReason(reason);
    setOverrideReasonExplanation(explanation);
    setValidationErrors({});
  };

  const handleCancelEdit = () => {
    setEditingQuestion(null);
    setEditValues({});
    setOverrideReason('');
    setOverrideReasonExplanation('');
    setValidationErrors({});
  };

  const handleSetToCorrectAnswer = (question) => {
    if (question.question_type === 'multiple_choice' || question.question_type === 'true_false') {
      // Find the correct option by checking is_correct flag
      let correctOption = question.options?.find(opt => opt.is_correct);
      
      // If not found by is_correct, try to match with correct_answer
      if (!correctOption && question.correct_answer && question.options) {
        correctOption = question.options.find(opt => 
          opt.id === question.correct_answer ||
          opt.option_id === question.correct_answer ||
          opt.option_text === question.correct_answer ||
          opt.text === question.correct_answer ||
          opt.optionText === question.correct_answer
        );
      }
      
      if (correctOption) {
        setEditValues({
          ...editValues,
          selected_option_id: correctOption.id || correctOption.option_id
        });
      }
    } else if (question.question_type === 'fill_blank' || question.question_type === 'fill_in_the_blank') {
      // Parse the correct answer for fill-in-the-blank
      if (question.correct_answer) {
        const blankCount = getBlankCount(question.question_prompt);
        const parsedCorrectAnswers = parseFillBlankAnswer(question.correct_answer);
        
        // Initialize answer_texts array with correct answers
        const answerTexts = Array(blankCount).fill('').map((_, index) => 
          parsedCorrectAnswers[index] || ''
        );
        
        setEditValues({
          ...editValues,
          answer_texts: answerTexts
        });
      }
    }
  };

  const handleSaveOverride = async (questionId) => {
    // Validate inputs
    const errors = {};
    
    if (!overrideReason) {
      errors.overrideReason = 'Override reason is required';
    }
    
    // If "Other" is selected, explanation is required
    if (overrideReason === 'Other' && !overrideReasonExplanation.trim()) {
      errors.overrideReasonExplanation = 'Please provide an explanation for the override';
    }
    
    const question = submission.data.feedback.find(f => f.question_id === questionId);
    
    if (question) {
      if (question.question_type === 'multiple_choice' || question.question_type === 'true_false') {
        if (!editValues.selected_option_id) {
          errors.answer = 'Please select an option';
        }
      } else if (question.question_type === 'fill_blank' || question.question_type === 'fill_in_the_blank') {
        // For fill-in-the-blank, check if we have answer_texts array
        if (editValues.answer_texts) {
          // Check if at least one blank is filled
          const hasAnswer = editValues.answer_texts.some(answer => answer && answer.trim());
          if (!hasAnswer) {
            errors.answer = 'At least one blank must be filled';
          }
        } else if (!editValues.answer_text || !editValues.answer_text.trim()) {
          errors.answer = 'Answer text is required';
        }
      } else if (question.question_type === 'matching') {
        // For matching, check if at least one pair is selected
        if (!editValues.matchingPairs || Object.keys(editValues.matchingPairs).length === 0) {
          errors.answer = 'Please match at least one item';
        } else {
          // Validate that all pairs have valid indices
          const pairs = Object.entries(editValues.matchingPairs);
          const invalidPairs = pairs.filter(([leftIndex, rightIndex]) => 
            isNaN(parseInt(leftIndex)) || rightIndex === undefined || rightIndex === null || isNaN(parseInt(rightIndex))
          );
          if (invalidPairs.length > 0) {
            errors.answer = 'Invalid matching pairs';
          }
        }
      } else {
        // For short answer or essay
        if (!editValues.answer_text || !editValues.answer_text.trim()) {
          errors.answer = 'Answer text is required';
        }
      }
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    try {
      setSaving(true);
      setValidationErrors({});
      
      // Format the answer properly for the API
      let answerText = editValues.answer_text;
      if (editValues.answer_texts) {
        answerText = formatFillBlankAnswer(editValues.answer_texts);
      } else if (question.question_type === 'matching') {
        // For matching questions, convert matchingPairs object to JSON array
        if (editValues.matchingPairs) {
          const pairs = Object.entries(editValues.matchingPairs)
            .filter(([leftIndex, rightIndex]) => rightIndex !== undefined && rightIndex !== null)
            .map(([leftIndex, rightIndex]) => ({
              leftIndex: parseInt(leftIndex),
              rightIndex: parseInt(rightIndex)
            }));
          answerText = JSON.stringify(pairs);
        } else {
          answerText = '[]';
        }
      }
      
      // Combine reason and explanation if "Other" is selected
      let finalReason = overrideReason;
      if (overrideReason === 'Other' && overrideReasonExplanation.trim()) {
        finalReason = `Other - ${overrideReasonExplanation.trim()}`;
      }
      
      const requestData = {
        answer_text: answerText,
        selected_option_id: editValues.selected_option_id,
        reason: finalReason
      };
      
      const attemptId = item.id; // This is the attempt_id
      const response = await quizApiService.overrideStudentAnswer(attemptId, questionId, requestData);

      if (response.success) {
        // Refresh submission data to get updated score
        await fetchSubmission();
        setEditingQuestion(null);
        setEditValues({});
        setOverrideReason('');
        setError(''); // Clear any previous errors
      } else {
        setError(response.message || 'Failed to override answer');
      }
    } catch (err) {
      console.error('Error overriding answer:', err);
      setError('Failed to override answer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              {type === 'assignment' ? 'Assignment Submission' : 'Quiz Submission'}
            </ModalTitle>
            <CloseButton onClick={onClose} aria-label="Close">
              <FiX size={24} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            {loading ? (
              <LoadingState>
                <FiClock size={32} />
                <p>Loading submission...</p>
              </LoadingState>
            ) : error ? (
              <ErrorState>
                <FiXCircle size={32} />
                <p>{error}</p>
              </ErrorState>
            ) : submission && submission.type === 'assignment' ? (
              <>
                <InfoSection>
                  <InfoRow>
                    <FiUser size={16} />
                    <InfoLabel>Student:</InfoLabel>
                    <InfoValue>{student.name}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <FiFileText size={16} />
                    <InfoLabel>Assignment:</InfoLabel>
                    <InfoValue>{submission.assignment?.title || item.title}</InfoValue>
                  </InfoRow>
                  {submission.data.submitted_at && (
                    <InfoRow>
                      <FiCalendar size={16} />
                      <InfoLabel>Submitted:</InfoLabel>
                      <InfoValue>{formatTimestamp(submission.data.submitted_at)}</InfoValue>
                    </InfoRow>
                  )}
                  {submission.data.grade !== null && submission.data.grade !== undefined && (
                    <InfoRow>
                      <FiAward size={16} />
                      <InfoLabel>Grade:</InfoLabel>
                      <InfoValue>
                        {submission.data.grade} / {submission.assignment?.points || item.max_points}
                      </InfoValue>
                    </InfoRow>
                  )}
                  {submission.data.status && (
                    <InfoRow>
                      {submission.data.status === 'graded' ? (
                        <FiCheckCircle size={16} />
                      ) : submission.data.status === 'submitted' ? (
                        <FiClock size={16} />
                      ) : (
                        <FiFileText size={16} />
                      )}
                      <InfoLabel>Status:</InfoLabel>
                      <InfoValue style={{ textTransform: 'capitalize' }}>
                        {submission.data.status}
                      </InfoValue>
                    </InfoRow>
                  )}
                </InfoSection>

                {submission.data.content && (
                  <SubmissionContent
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtmlForViewer(
                        typeof submission.data.content === 'string'
                          ? submission.data.content
                          : submission.data.content.html || ''
                      )
                    }}
                  />
                )}

                {submission.data.pdf_attachment && (
                  <PdfButtonContainer>
                    <PdfButton onClick={() => handleViewPdf(submission.data.pdf_attachment)}>
                      <FiFileText size={16} />
                      View PDF Attachment
                    </PdfButton>
                    <DownloadButton onClick={() => handleDownloadPdf(submission.data.pdf_attachment)}>
                      <FiDownload size={16} />
                      Download PDF Attachment
                    </DownloadButton>
                  </PdfButtonContainer>
                )}

                {submission.data.feedback && (
                  <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(246, 224, 94, 0.1)', borderRadius: 0}}>
                    <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'var(--warning-dark)' }}>
                      Feedback:
                    </div>
                    <div style={{ color: 'var(--warning-dark)' }}>{submission.data.feedback}</div>
                  </div>
                )}
              </>
            ) : submission && submission.type === 'quiz' ? (
              <>
                <InfoSection>
                  <InfoRow>
                    <FiUser size={16} />
                    <InfoLabel>Student:</InfoLabel>
                    <InfoValue>{submission.data.student_name || student.name}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <FiFileText size={16} />
                    <InfoLabel>Quiz:</InfoLabel>
                    <InfoValue>{submission.data.quiz_title || item.title}</InfoValue>
                  </InfoRow>
                  {submission.data.started_at && (
                    <InfoRow>
                      <FiClock size={16} />
                      <InfoLabel>Started:</InfoLabel>
                      <InfoValue>{formatTimestamp(submission.data.started_at)}</InfoValue>
                    </InfoRow>
                  )}
                  {submission.data.submitted_at && (
                    <InfoRow>
                      <FiCalendar size={16} />
                      <InfoLabel>Submitted:</InfoLabel>
                      <InfoValue>{formatTimestamp(submission.data.submitted_at)}</InfoValue>
                    </InfoRow>
                  )}
                  {submission.data.score !== null && submission.data.score !== undefined && (
                    <InfoRow>
                      <FiAward size={16} />
                      <InfoLabel>Score:</InfoLabel>
                      <InfoValue>
                        {submission.data.score} / {submission.data.max_score}
                        {submission.data.max_score > 0 && (
                          <span style={{ marginLeft: '0.5rem', color: 'var(--text-muted)' }}>
                            ({Math.round((submission.data.score / submission.data.max_score) * 100)}%)
                          </span>
                        )}
                      </InfoValue>
                    </InfoRow>
                  )}
                </InfoSection>

                {/* Filters */}
                {submission.data.feedback && submission.data.feedback.length > 0 && (
                  <FiltersContainer>
                    <FilterRow>
                      <FilterLabel>Show:</FilterLabel>
                      <FilterButtonGroup>
                        <FilterButton
                          active={answerFilter === 'all'}
                          onClick={() => setAnswerFilter('all')}
                          type="button"
                        >
                          All
                        </FilterButton>
                        <FilterButton
                          active={answerFilter === 'correct'}
                          onClick={() => setAnswerFilter('correct')}
                          type="button"
                        >
                          Correct
                        </FilterButton>
                        <FilterButton
                          active={answerFilter === 'incorrect'}
                          onClick={() => setAnswerFilter('incorrect')}
                          type="button"
                        >
                          Incorrect
                        </FilterButton>
                      </FilterButtonGroup>
                    </FilterRow>
                    <FilterRow>
                      <FilterLabel>Show by Question Type:</FilterLabel>
                      <FilterButtonGroup>
                        <FilterButton
                          active={questionTypeFilter === 'all'}
                          onClick={() => setQuestionTypeFilter('all')}
                          type="button"
                        >
                          All
                        </FilterButton>
                        <FilterButton
                          active={questionTypeFilter === 'matching'}
                          onClick={() => setQuestionTypeFilter('matching')}
                          type="button"
                        >
                          Matching
                        </FilterButton>
                        <FilterButton
                          active={questionTypeFilter === 'fill_blank'}
                          onClick={() => setQuestionTypeFilter('fill_blank')}
                          type="button"
                        >
                          Fill In the Blank
                        </FilterButton>
                        <FilterButton
                          active={questionTypeFilter === 'true_false'}
                          onClick={() => setQuestionTypeFilter('true_false')}
                          type="button"
                        >
                          True/False
                        </FilterButton>
                        <FilterButton
                          active={questionTypeFilter === 'multiple_choice'}
                          onClick={() => setQuestionTypeFilter('multiple_choice')}
                          type="button"
                        >
                          Multiple Choice
                        </FilterButton>
                      </FilterButtonGroup>
                    </FilterRow>
                    <FilterRow>
                      <FilterLabel>Show by Credit Type:</FilterLabel>
                      <FilterButtonGroup>
                        <FilterButton
                          active={extraCreditFilter === 'all'}
                          onClick={() => setExtraCreditFilter('all')}
                          type="button"
                        >
                          All
                        </FilterButton>
                        <FilterButton
                          active={extraCreditFilter === 'extra_credit'}
                          onClick={() => setExtraCreditFilter('extra_credit')}
                          type="button"
                        >
                          Extra Credit
                        </FilterButton>
                        <FilterButton
                          active={extraCreditFilter === 'regular'}
                          onClick={() => setExtraCreditFilter('regular')}
                          type="button"
                        >
                          Regular
                        </FilterButton>
                      </FilterButtonGroup>
                    </FilterRow>
                  </FiltersContainer>
                )}

                {submission.data.feedback && submission.data.feedback.length > 0 && (() => {
                  // Filter questions based on selected filters
                  let filteredQuestions = submission.data.feedback;
                  
                  // Filter by answer correctness
                  if (answerFilter === 'correct') {
                    filteredQuestions = filteredQuestions.filter(q => q.is_correct === true);
                  } else if (answerFilter === 'incorrect') {
                    filteredQuestions = filteredQuestions.filter(q => q.is_correct === false || q.is_correct === null);
                  }
                  
                  // Filter by question type
                  if (questionTypeFilter !== 'all') {
                    filteredQuestions = filteredQuestions.filter(q => {
                      if (questionTypeFilter === 'matching') {
                        return q.question_type === 'matching';
                      } else if (questionTypeFilter === 'fill_blank') {
                        return q.question_type === 'fill_blank' || q.question_type === 'fill_in_the_blank';
                      } else if (questionTypeFilter === 'true_false') {
                        return q.question_type === 'true_false';
                      } else if (questionTypeFilter === 'multiple_choice') {
                        return q.question_type === 'multiple_choice';
                      }
                      return true;
                    });
                  }
                  
                  // Filter by extra credit
                  if (extraCreditFilter === 'extra_credit') {
                    filteredQuestions = filteredQuestions.filter(q => q.extra_credit === true);
                  } else if (extraCreditFilter === 'regular') {
                    filteredQuestions = filteredQuestions.filter(q => !q.extra_credit || q.extra_credit === false);
                  }
                  
                  return filteredQuestions.length > 0 ? (
                    <div style={{ marginTop: '1.5rem' }}>
                      {filteredQuestions.map((question, index) => {
                      // Format student answer based on question type
                      const formatStudentAnswer = () => {
                        if (question.question_type === 'multiple_choice' || question.question_type === 'true_false') {
                          // For multiple choice, find the selected option
                          if (question.options && question.your_answer) {
                            const selectedOption = question.options.find(opt => 
                              opt.id === question.your_answer || opt.option_text === question.your_answer || opt.text === question.your_answer
                            );
                            return selectedOption ? (selectedOption.option_text || selectedOption.text) : question.your_answer;
                          }
                          return question.your_answer || 'No answer provided';
                        } else if (question.question_type === 'fill_in_the_blank' || question.question_type === 'fill_blank') {
                          // For fill in the blank, format the prompt with answers
                          if (question.question_prompt && question.your_answer) {
                            let formatted = question.question_prompt;
                            const answers = Array.isArray(question.your_answer) 
                              ? question.your_answer 
                              : (typeof question.your_answer === 'string' && question.your_answer.includes('|')
                                  ? question.your_answer.split('|')
                                  : [question.your_answer]);
                            let answerIndex = 0;
                            formatted = formatted.replace(/_/g, () => {
                              if (answerIndex < answers.length) {
                                const answer = answers[answerIndex++];
                                return answer ? `<strong style="color: var(--text-primary); background: rgba(148, 163, 184, 0.1); padding: 0.25rem 0.5rem; border-radius: 0;">${answer}</strong>` : '________';
                              }
                              return '________';
                            });
                            return formatted;
                          }
                          return question.your_answer || 'No answer provided';
                        } else if (question.question_type === 'matching') {
                          // For matching questions, format the pairs
                          try {
                            let pairs = [];
                            if (Array.isArray(question.your_answer)) {
                              // If already an array of objects with left/right
                              pairs = question.your_answer;
                            } else if (typeof question.your_answer === 'string') {
                              // Parse JSON string
                              pairs = JSON.parse(question.your_answer);
                            }
                            
                            if (Array.isArray(pairs) && pairs.length > 0) {
                              // Check if pairs have left/right properties (already formatted)
                              if (pairs[0].left && pairs[0].right) {
                                return pairs.map((pair, index) => {
                                  const isCorrect = pair.isCorrect !== false;
                                  const iconColor = isCorrect ? 'var(--success-color)' : 'var(--error-color)';
                                  const icon = isCorrect ? '✓' : '✗';
                                  return `
                                    <div style="margin-bottom: 0.75rem; padding: 0.75rem; background: ${isCorrect ? 'rgba(22, 163, 74, 0.05)' : 'rgba(220, 38, 38, 0.05)'}; border-left: 3px solid ${iconColor}; border-radius: 0;">
                                      <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.25rem;">
                                        <span style="color: ${iconColor}; font-weight: bold;">${icon}</span>
                                        <strong>Term:</strong> <span>${sanitizeHtmlForViewer(pair.left)}</span>
                                      </div>
                                      <div style="margin-left: 1.5rem;">
                                        <strong>Definition:</strong> <span>${sanitizeHtmlForViewer(pair.right)}</span>
                                      </div>
                                    </div>
                                  `;
                                }).join('');
                              } else {
                                // Pairs are indices, need to map to actual items
                                const settings = question.settings || {};
                                const leftItems = settings.leftItems || [];
                                const rightItems = settings.rightItems || [];
                                
                                if (leftItems.length > 0 && rightItems.length > 0) {
                                  return pairs.map((pair, index) => {
                                    const leftIndex = pair.leftIndex;
                                    const rightIndex = pair.rightIndex;
                                    const leftItem = leftItems[leftIndex] || 'N/A';
                                    const rightItem = rightItems[rightIndex] || 'N/A';
                                    return `
                                      <div style="margin-bottom: 0.75rem; padding: 0.75rem; background: var(--background-secondary); border-left: 3px solid var(--border-color); border-radius: 0;">
                                        <div style="margin-bottom: 0.25rem;">
                                          <strong>Term:</strong> <span>${sanitizeHtmlForViewer(leftItem)}</span>
                                        </div>
                                        <div style="margin-left: 1.5rem;">
                                          <strong>Definition:</strong> <span>${sanitizeHtmlForViewer(rightItem)}</span>
                                        </div>
                                      </div>
                                    `;
                                  }).join('');
                                }
                              }
                            }
                            return 'No matches provided';
                          } catch (e) {
                            console.error('Error formatting matching answer:', e);
                            return question.your_answer || 'No answer provided';
                          }
                        } else {
                          // For short answer or essay
                          return question.your_answer || 'No answer provided';
                        }
                      };

                      const isEditing = editingQuestion === question.question_id;

                      return (
                        <QuestionContainer key={question.question_id || index}>
                          <QuestionHeader>
                            <QuestionNumber>{index + 1}</QuestionNumber>
                            <QuestionText
                              dangerouslySetInnerHTML={{
                                __html: sanitizeHtmlForViewer(question.question_prompt || '')
                              }}
                            />
                            <div style={{ 
                              marginLeft: 'auto',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}>
                              {question.extra_credit && (
                                <span style={{ 
                                  padding: '0.25rem 0.5rem',
                                  background: 'rgba(246, 224, 94, 0.1)',
                                  color: 'var(--warning-dark)',
                                  borderRadius: 0,
                                  fontSize: '0.75rem',
                                  fontWeight: 600
                                }}>
                                  Extra Credit
                                </span>
                              )}
                              {question.is_correct !== undefined && question.is_correct !== null && (
                                <CorrectBadge className={question.is_correct ? 'correct' : 'incorrect'}>
                                  {question.is_correct ? (
                                    <>
                                      <FiCheckCircle size={12} />
                                      Correct
                                    </>
                                  ) : (
                                    <>
                                      <FiXCircle size={12} />
                                      Incorrect
                                    </>
                                  )}
                                </CorrectBadge>
                              )}
                              <span style={{ 
                                fontSize: '0.875rem',
                                color: 'var(--text-muted)',
                                fontWeight: 600
                              }}>
                                {question.points_awarded !== undefined && question.points_awarded !== null
                                  ? `${question.points_awarded}`
                                  : '0'}/{question.max_points || question.points || 0} pts
                              </span>
                              {!isEditing && (
                                <EditButton onClick={() => handleEditQuestion(question)}>
                                  <FiEdit3 size={14} />
                                  Edit
                                </EditButton>
                              )}
                            </div>
                          </QuestionHeader>
                          <AnswerSection>
                            {isEditing ? (
                              <>
                                <AnswerLabel>Edit Answer:<RequiredField>*</RequiredField></AnswerLabel>
                                
                                {question.question_type === 'multiple_choice' || question.question_type === 'true_false' ? (
                                  <RadioGroup>
                                    {question.options?.map((option, optIndex) => (
                                      <RadioOption 
                                        key={option.id || option.option_id || optIndex}
                                        className={editValues.selected_option_id === (option.id || option.option_id) ? 'selected' : ''}
                                      >
                                        <RadioInput
                                          type="radio"
                                          name={`question_${question.question_id}`}
                                          value={option.id || option.option_id}
                                          checked={editValues.selected_option_id === (option.id || option.option_id)}
                                          onChange={(e) => setEditValues({...editValues, selected_option_id: parseInt(e.target.value)})}
                                        />
                                        <RadioText>{option.option_text || option.text || option.optionText}</RadioText>
                                      </RadioOption>
                                    ))}
                                  </RadioGroup>
                                ) : question.question_type === 'fill_blank' || question.question_type === 'fill_in_the_blank' ? (
                                  <FillBlankEditContainer>
                                    {editValues.answer_texts ? (
                                      editValues.answer_texts.map((answer, blankIndex) => (
                                        <FillBlankEditField key={blankIndex}>
                                          <FillBlankLabel>Blank Answer {blankIndex + 1}:</FillBlankLabel>
                                          <EditInput
                                            value={answer}
                                            onChange={(e) => {
                                              const newAnswers = [...editValues.answer_texts];
                                              newAnswers[blankIndex] = e.target.value;
                                              setEditValues({...editValues, answer_texts: newAnswers});
                                            }}
                                            placeholder={`Enter answer for blank ${blankIndex + 1}...`}
                                            className={validationErrors.answer ? 'error' : ''}
                                          />
                                        </FillBlankEditField>
                                      ))
                                    ) : (
                                      <EditTextarea
                                        value={editValues.answer_text || ''}
                                        onChange={(e) => setEditValues({...editValues, answer_text: e.target.value})}
                                        placeholder="Enter the answer..."
                                        className={validationErrors.answer ? 'error' : ''}
                                      />
                                    )}
                                  </FillBlankEditContainer>
                                ) : question.question_type === 'matching' ? (
                                  (() => {
                                    const settings = question.settings || {};
                                    const leftItems = settings.leftItems || [];
                                    const rightItems = settings.rightItems || [];
                                    
                                    if (leftItems.length === 0 || rightItems.length === 0) {
                                      return (
                                        <div style={{ 
                                          padding: '1rem', 
                                          background: 'rgba(246, 224, 94, 0.1)', 
                                          borderRadius: 0,
                                          color: 'var(--warning-dark)',
                                          fontSize: '0.875rem'
                                        }}>
                                          Question settings not available. Please use JSON format to edit.
                                        </div>
                                      );
                                    }
                                    
                                    // Filter out empty items
                                    const validLeftItems = leftItems.map((item, index) => ({ item, index })).filter(({ item }) => item && item.trim());
                                    const validRightItems = rightItems.map((item, index) => ({ item, index })).filter(({ item }) => item && item.trim());
                                    
                                    return (
                                      <MatchingEditContainer>
                                        {validLeftItems.map(({ item: leftItem, index: leftIndex }) => {
                                          const currentRightIndex = editValues.matchingPairs?.[leftIndex] !== undefined 
                                            ? editValues.matchingPairs[leftIndex] 
                                            : '';
                                          
                                          return (
                                            <MatchingEditRow key={leftIndex}>
                                              <MatchingTerm>{leftItem}</MatchingTerm>
                                              <MatchingSelect
                                                value={currentRightIndex}
                                                onChange={(e) => {
                                                  const newRightIndex = e.target.value === '' ? undefined : parseInt(e.target.value);
                                                  const newPairs = { ...editValues.matchingPairs };
                                                  
                                                  if (newRightIndex === undefined) {
                                                    delete newPairs[leftIndex];
                                                  } else {
                                                    newPairs[leftIndex] = newRightIndex;
                                                  }
                                                  
                                                  setEditValues({...editValues, matchingPairs: newPairs});
                                                  
                                                  // Clear validation error when user makes a selection
                                                  if (validationErrors.answer) {
                                                    setValidationErrors({...validationErrors, answer: undefined});
                                                  }
                                                }}
                                                className={validationErrors.answer ? 'error' : ''}
                                              >
                                                <option value="">-- Select Definition --</option>
                                                {validRightItems.map(({ item: rightItem, index: rightIndex }) => (
                                                  <option key={rightIndex} value={rightIndex}>
                                                    {rightItem}
                                                  </option>
                                                ))}
                                              </MatchingSelect>
                                            </MatchingEditRow>
                                          );
                                        })}
                                      </MatchingEditContainer>
                                    );
                                  })()
                                ) : (
                                  <EditTextarea
                                    value={editValues.answer_text || ''}
                                    onChange={(e) => setEditValues({...editValues, answer_text: e.target.value})}
                                    placeholder="Enter the answer..."
                                    className={validationErrors.answer ? 'error' : ''}
                                    style={{ minHeight: '100px' }}
                                  />
                                )}
                                
                                {validationErrors.answer && (
                                  <ErrorMessage>{validationErrors.answer}</ErrorMessage>
                                )}
                                
                                {/* Quick shortcut to set answer to correct answer */}
                                {(question.question_type === 'multiple_choice' || question.question_type === 'true_false' || question.question_type === 'fill_blank' || question.question_type === 'fill_in_the_blank') && 
                                 question.correct_answer && (
                                  <div style={{ marginTop: '0.75rem', marginBottom: '0.5rem' }}>
                                    <EditButton
                                      onClick={() => handleSetToCorrectAnswer(question)}
                                      style={{ 
                                        background: 'var(--success-color)', 
                                        borderColor: 'var(--success-color)',
                                        fontSize: '0.8125rem',
                                        padding: '0.5rem 0.75rem'
                                      }}
                                      type="button"
                                    >
                                      <FiCheck size={14} />
                                      Set to Correct Answer
                                    </EditButton>
                                  </div>
                                )}
                                
                                <AnswerLabel style={{ marginTop: '1rem' }}>Override Reason:<RequiredField>*</RequiredField></AnswerLabel>
                                <EditSelect
                                  value={overrideReason}
                                  onChange={(e) => {
                                    setOverrideReason(e.target.value);
                                    // Clear explanation when switching away from "Other"
                                    if (e.target.value !== 'Other') {
                                      setOverrideReasonExplanation('');
                                    }
                                    // Clear validation errors
                                    if (validationErrors.overrideReason) {
                                      setValidationErrors({...validationErrors, overrideReason: undefined});
                                    }
                                    if (validationErrors.overrideReasonExplanation) {
                                      setValidationErrors({...validationErrors, overrideReasonExplanation: undefined});
                                    }
                                  }}
                                  className={validationErrors.overrideReason ? 'error' : ''}
                                >
                                  <option value="">Select a reason...</option>
                                  <option value="Question Error">Question Error – The question or answer key contained an error.</option>
                                  <option value="Ambiguous Question">Ambiguous Question – Multiple interpretations could reasonably be correct.</option>
                                  <option value="Alternative Correct Answer">Alternative Correct Answer – The student provided a valid response not in the key.</option>
                                  <option value="Partial Credit Awarded">Partial Credit Awarded – Student demonstrated understanding deserving partial credit.</option>
                                  <option value="Technical Issue">Technical Issue – A system or submission issue affected the student's response.</option>
                                  <option value="Manual Review Required">Manual Review Required – Auto-grading was insufficient or invalid for this item.</option>
                                  <option value="Student Accommodation">Student Accommodation – Adjustment made based on approved accommodations.</option>
                                  <option value="Exceptional Circumstance">Exceptional Circumstance – Special situation requiring instructor discretion.</option>
                                  <option value="Removed or Invalidated Question">Removed or Invalidated Question – Question later deemed unscorable or unfair.</option>
                                  <option value="Other">Other - General override when none of the above apply.</option>
                                </EditSelect>
                                {validationErrors.overrideReason && (
                                  <ErrorMessage>{validationErrors.overrideReason}</ErrorMessage>
                                )}
                                
                                {overrideReason === 'Other' && (
                                  <>
                                    <AnswerLabel style={{ marginTop: '1rem' }}>Explanation:<RequiredField>*</RequiredField></AnswerLabel>
                                    <EditTextarea
                                      value={overrideReasonExplanation}
                                      onChange={(e) => {
                                        setOverrideReasonExplanation(e.target.value);
                                        // Clear validation error when user starts typing
                                        if (validationErrors.overrideReasonExplanation) {
                                          setValidationErrors({...validationErrors, overrideReasonExplanation: undefined});
                                        }
                                      }}
                                      placeholder="Please explain why you're overriding this answer..."
                                      className={validationErrors.overrideReasonExplanation ? 'error' : ''}
                                      style={{ minHeight: '80px' }}
                                    />
                                    {validationErrors.overrideReasonExplanation && (
                                      <ErrorMessage>{validationErrors.overrideReasonExplanation}</ErrorMessage>
                                    )}
                                  </>
                                )}

                                <EditActions>
                                  <EditButton 
                                    onClick={() => handleSaveOverride(question.question_id)}
                                    disabled={saving}
                                  >
                                    <FiSave size={14} />
                                    {saving ? 'Saving...' : 'Save Override'}
                                  </EditButton>
                                  <EditButton 
                                    onClick={handleCancelEdit}
                                    style={{ background: 'var(--text-muted)', borderColor: 'var(--text-muted)' }}
                                  >
                                    <FiX size={14} />
                                    Cancel
                                  </EditButton>
                                </EditActions>
                              </>
                            ) : (
                              <>
                                <AnswerLabel>Student Answer:</AnswerLabel>
                                <AnswerContent
                                  dangerouslySetInnerHTML={{
                                    __html: sanitizeHtmlForViewer(formatStudentAnswer())
                                  }}
                                />
                                {question.correct_answer && (
                                  <>
                                    <AnswerLabel style={{ marginTop: '1rem' }}>Correct Answer:</AnswerLabel>
                                    <AnswerContent
                                      dangerouslySetInnerHTML={{
                                        __html: sanitizeHtmlForViewer(
                                          (() => {
                                            if (question.question_type === 'multiple_choice' || question.question_type === 'true_false') {
                                              return question.options?.find(opt => opt.is_correct)?.option_text || question.options?.find(opt => opt.is_correct)?.text || question.correct_answer;
                                            } else if (question.question_type === 'matching') {
                                              // Format correct answer for matching questions
                                              try {
                                                let correctPairs = [];
                                                if (Array.isArray(question.correct_answer)) {
                                                  correctPairs = question.correct_answer;
                                                } else if (typeof question.correct_answer === 'string') {
                                                  correctPairs = JSON.parse(question.correct_answer);
                                                }
                                                
                                                if (Array.isArray(correctPairs) && correctPairs.length > 0) {
                                                  // Check if pairs have left/right properties
                                                  if (correctPairs[0].left && correctPairs[0].right) {
                                                    return correctPairs.map((pair) => `
                                                      <div style="margin-bottom: 0.75rem; padding: 0.75rem; background: rgba(104, 211, 145, 0.1); border-left: 3px solid var(--success-color); border-radius: 0;">
                                                        <div style="margin-bottom: 0.25rem;">
                                                          <strong>Term:</strong> <span>${sanitizeHtmlForViewer(pair.left)}</span>
                                                        </div>
                                                        <div style="margin-left: 1.5rem;">
                                                          <strong>Definition:</strong> <span>${sanitizeHtmlForViewer(pair.right)}</span>
                                                        </div>
                                                      </div>
                                                    `).join('');
                                                  } else {
                                                    // Pairs are indices, need to map to actual items
                                                    const settings = question.settings || {};
                                                    const leftItems = settings.leftItems || [];
                                                    const rightItems = settings.rightItems || [];
                                                    
                                                    if (leftItems.length > 0 && rightItems.length > 0) {
                                                      return correctPairs.map((pair) => {
                                                        const leftIndex = pair.leftIndex;
                                                        const rightIndex = pair.rightIndex;
                                                        const leftItem = leftItems[leftIndex] || 'N/A';
                                                        const rightItem = rightItems[rightIndex] || 'N/A';
                                                        return `
                                                          <div style="margin-bottom: 0.75rem; padding: 0.75rem; background: rgba(104, 211, 145, 0.1); border-left: 3px solid var(--success-color); border-radius: 0;">
                                                            <div style="margin-bottom: 0.25rem;">
                                                              <strong>Term:</strong> <span>${sanitizeHtmlForViewer(leftItem)}</span>
                                                            </div>
                                                            <div style="margin-left: 1.5rem;">
                                                              <strong>Definition:</strong> <span>${sanitizeHtmlForViewer(rightItem)}</span>
                                                            </div>
                                                          </div>
                                                        `;
                                                      }).join('');
                                                    }
                                                  }
                                                }
                                                return question.correct_answer;
                                              } catch (e) {
                                                console.error('Error formatting correct matching answer:', e);
                                                return question.correct_answer;
                                              }
                                            }
                                            return question.correct_answer;
                                          })()
                                        )
                                      }}
                                    />
                                  </>
                                )}
                              </>
                            )}
                          </AnswerSection>
                        </QuestionContainer>
                      );
                    })}
                    </div>
                  ) : (
                    <div style={{ 
                      marginTop: '1.5rem', 
                      padding: '2rem', 
                      textAlign: 'center',
                      background: 'var(--background-secondary)',
                      borderRadius: 0,
                      border: '1px solid var(--border-color)'
                    }}>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 0 }}>
                        No questions match the selected filters.
                      </p>
                    </div>
                  );
                })()}
              </>
            ) : null}
          </ModalBody>
        </ModalContent>
      </ModalOverlay>

      {showPdfViewer && selectedPdf && (
        <PdfViewer
          isOpen={showPdfViewer}
          pdfBase64={selectedPdf.pdfBase64}
          fileName={selectedPdf.fileName}
          onClose={() => {
            setShowPdfViewer(false);
            setSelectedPdf(null);
          }}
        />
      )}
    </>
  );
}

export default SubmissionViewModal;

